// components/ImageSelector/SelectionPanel.tsx
import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { Button } from "@/components/ui/Button";
import { UnifiedAuthenticatedImage } from "@/components/ui/UnifiedAuthenticatedImage";
import { ChevronDownIcon } from "@/components/icons";
import type { CropSelection } from "./MediaCropModal";

interface SelectionPanelProps {
  selectedMediaItems: MediaItem[];
  isCarousel: boolean;
  onRemove: (imageId: number) => void;
  onClear: () => void;
  onReorder?: (newOrder: MediaItem[]) => void;
  crops?: Record<number, CropSelection>;
  onCrop?: (item: MediaItem) => void;
}

export const SelectionPanel: React.FC<SelectionPanelProps> = ({
  selectedMediaItems,
  isCarousel,
  onRemove,
  onClear,
  onReorder,
  crops = {},
  onCrop,
}) => {
  const handleMoveUp = (index: number) => {
    if (index === 0 || !onReorder) return;
    const newOrder = [...selectedMediaItems];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    onReorder(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index === selectedMediaItems.length - 1 || !onReorder) return;
    const newOrder = [...selectedMediaItems];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    onReorder(newOrder);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">
          Selected ({selectedMediaItems.length})
          {!isCarousel && (
            <span className="text-xs text-gray-500 ml-1">(Single)</span>
          )}
          {isCarousel && (
            <span className="text-xs text-gray-500 ml-1">(Carousel)</span>
          )}
        </h3>
        {selectedMediaItems.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear All
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {selectedMediaItems.map((image, index) => {
          const hasCrop = Boolean(crops[image.id]);
          const isPhoto = image.mediaType === "photo";
          const thumbnailClickable = isPhoto && Boolean(onCrop);
          return (
          <div
            key={`${image.id}-${index}`} // Use compound key to force re-render on reorder
            className="flex items-center space-x-3 p-2 border rounded-lg bg-white"
          >
            <span className="shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
              {isCarousel ? index + 1 : "✓"}
            </span>
            <button
              type="button"
              onClick={
                thumbnailClickable ? () => onCrop!(image) : undefined
              }
              disabled={!thumbnailClickable}
              className={`relative shrink-0 rounded-sm overflow-hidden ${
                thumbnailClickable
                  ? "cursor-pointer hover:ring-2 hover:ring-blue-400 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
                  : "cursor-default"
              }`}
              title={
                thumbnailClickable
                  ? hasCrop
                    ? "Edit 4:5 crop"
                    : "Adjust 4:5 crop"
                  : undefined
              }
            >
              <UnifiedAuthenticatedImage
                mediaItem={image}
                alt={image.originalFilename}
                className="w-12 h-12 object-cover"
                useThumbnail={true}
                key={`img-${image.id}-${index}`}
              />
              {hasCrop && (
                <span className="absolute bottom-0 right-0 px-1 text-[10px] leading-tight font-semibold bg-blue-600 text-white rounded-tl-sm">
                  4:5
                </span>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {image.originalFilename}
              </p>
              {index === 0 && (
                <p className="text-xs text-gray-500">Main image</p>
              )}
              {thumbnailClickable && (
                <button
                  type="button"
                  onClick={() => onCrop!(image)}
                  className="mt-0.5 text-xs text-blue-600 hover:text-blue-700"
                >
                  {hasCrop ? "Edit crop" : "Adjust crop"}
                </button>
              )}
            </div>

            {/* Reorder buttons for carousel */}
            {isCarousel && selectedMediaItems.length > 1 && onReorder && (
              <div className="flex flex-col space-y-1">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move up"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === selectedMediaItems.length - 1}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move down"
                >
                  <ChevronDownIcon className="w-3 h-3" />
                </button>
              </div>
            )}

            <button
              onClick={() => onRemove(image.id)}
              className="shrink-0 text-gray-400 hover:text-red-500 transition-colors"
              title="Remove image"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
};
